import { useState, type FormEvent } from 'react';
import { Loader2, Smartphone } from 'lucide-react';
import { normalizeGuestPhone } from '@hotel-voice/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toUserMessage } from '@/utils/errors';

interface PhoneGateDialogProps {
  onSubmit: (phone: string) => Promise<void>;
}

/**
 * The popup a guest meets on landing: the mobile number given at check-in.
 *
 * It cannot be dismissed — no close button, no click-away, no Escape —
 * because there is nothing behind it to fall back to; the page is not usable
 * until the number matches the booking. The number is normalised with the
 * same function `checkIn` keyed the register on, so "98765 43210" and
 * "+91 98765-43210" are the same guest here as they were at the desk.
 *
 * A match closes the dialog (the session becomes ready and the router mounts).
 * A mismatch stays here as an inline error — a mistyped digit should cost a
 * retry, not a trip back to the QR code — and so does anything else that
 * went wrong (offline, a hiccup). The message for a mismatch comes from the
 * server's code, and is the same whether the number was wrong or the room
 * has no booking at all.
 */
export function PhoneGateDialog({ onSubmit }: PhoneGateDialogProps) {
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;

    const normalized = normalizeGuestPhone(phone);
    if (!normalized) {
      setError('Enter the full mobile number, including the country code if outside India.');
      return;
    }

    setError(null);
    setBusy(true);
    try {
      await onSubmit(normalized);
    } catch (failure) {
      setError(toUserMessage(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open>
      <DialogContent
        showCloseButton={false}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        <form onSubmit={handleSubmit} noValidate className="grid gap-4">
          <DialogHeader className="items-center sm:items-start">
            <div className="bg-primary/10 text-primary mb-1 flex size-12 items-center justify-center rounded-full">
              <Smartphone className="size-6" aria-hidden="true" />
            </div>
            <DialogTitle>Verify your stay</DialogTitle>
            <DialogDescription>
              Enter the mobile number you gave at check-in to reach reception and hotel services.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-2">
            <Label htmlFor="guest-phone">Mobile number</Label>
            <Input
              id="guest-phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              autoFocus
              placeholder="98765 43210"
              value={phone}
              onChange={(event) => {
                setPhone(event.target.value);
                if (error) setError(null);
              }}
              disabled={busy}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'guest-phone-error' : undefined}
            />
            {error && (
              <p id="guest-phone-error" role="alert" className="text-destructive text-sm">
                {error}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button type="submit" className="w-full sm:w-auto" disabled={busy} aria-busy={busy}>
              {busy ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                  Checking…
                </>
              ) : (
                'Continue'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
